// EDIT BY ALI FOR DARK MODE - Added useTheme import
import {Avatar, Box, Grid, Link, Tooltip, Typography, useTheme} from "@mui/material";
import React, {useEffect, useState} from "react";
import Global from "../../../assets/global.svg";
import people from "../../../assets/people.svg";
import {BlackDotGrid, ChipCard, ChipCardSecond,} from "./style";
import defaultImageUrl from "../../../assets/defaultImage.svg";
import PlaceIcon from '@mui/icons-material/Place';
import ComingSoonDialog from "../../../components/Common/ComingSoonDialog";

import {addBackground, loadJimp} from "../../../helper/image_processing";
import CompanyModal from "../../../components/CompanyModal/CompanyModal";
import {registerCompanyInquiry} from "../../../api/companyApi";
import {ESTIMATED_LABEL, ESTIMATED_TOOLTIP, formatHeadcountBucket, isEstimatedMetric} from "../../../helper/metricBuckets";
import {labelForLocation} from "../../../helper/address";
import { MOBILE_TYPE } from "../../../theme/mobileLayout";


const ListCard = ({ company, compact = false, mobile = false }) => {
  const [open, setOpen] = useState(false);
  const [imageData, setImageData] = useState(defaultImageUrl);
  const [isUnimplemented, setIsUnimplemented] = useState(false)

  const handlePopUp = async (stagedId) => {
    registerCompanyInquiry(stagedId).then(setOpen(true))
  };

  const {
    primaryLogo,
    primaryName,
    primaryWebsite,
    harmonizedHeadcount,
    primaryLocation,
    descriptive,
  } = company;

  useEffect(() => {
    const getCompanyLogo = async (uri) => {
      if (!uri) return
      const unprocessed_uri = uri.replace("w_70,h_70,c_fit", "")
      const logoSize = compact ? 55 : 70;
      const Jimp = await loadJimp();

      await Jimp.read(unprocessed_uri)
        .then(image => image
          .autocrop()
          .scaleToFit(logoSize, logoSize))
        .then(processed =>
          processed.getBase64Async(Jimp.MIME_PNG))
        .then(imageString => {
          const bgSize = compact ? 59 : 74;
          addBackground(imageString, bgSize, bgSize)
          .then(withBg => {
            if (withBg) {
              setImageData(withBg)
            } else {
              setImageData(imageString)
            }
          })
        })
        .catch((err) => {
          console.log(err)
        });
    }
    getCompanyLogo(primaryLogo)
  }, [primaryLogo, compact]);

  // EDIT BY ALI FOR DARK MODE - Updated cardStyles to use theme
  const theme = useTheme();
  const industryLabel =
    company?.primaryIndustry
    || company?.industry
    || (Array.isArray(company?.classification) && company.classification[0]?.label)
    || null;

  const cardStyles = mobile
    ? {
      marginBottom: "12px",
      background: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(88, 89, 91, 0.16)"}`,
      borderRadius: "12px",
      padding: "14px 14px",
      cursor: "pointer",
      maxWidth: "100%",
      width: "100%",
    }
    : {
      marginBottom: compact ? "21px" : "28px",
      background: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(88, 89, 91, 0.2)"}`,
      borderRadius: "10px",
      padding: compact ? "20px 75px 20px 19px" : "26px 98px 26px 25px",
      cursor: "pointer",
      maxWidth: compact ? "943px" : "1257px",
      width: "100%",
    };

  const logoSize = mobile ? 48 : (compact ? 59 : 74);
  const containerGap = compact ? "15px" : "20px";
  const itemGap = compact ? "10px" : "13px";
  const titleFontSize = mobile ? MOBILE_TYPE.cardTitle.fontSize : (compact ? 15 : 20);
  const websiteFontSize = mobile ? 12 : (compact ? 12 : 16);
  const descriptionFontSize = mobile ? MOBILE_TYPE.secondary.fontSize : (compact ? 14 : 18);
  const descriptionLineHeight = mobile ? "18px" : (compact ? "21px" : "28px");
  const descriptionMaxLength = mobile ? 110 : (compact ? 180 : 240);

  const headcountEstimated = Boolean(formatHeadcountBucket(harmonizedHeadcount)) && isEstimatedMetric(harmonizedHeadcount);
  const headcountSuffix = headcountEstimated ? ` (${ESTIMATED_LABEL})` : "";

  if (mobile) {
    const headcountBucket = formatHeadcountBucket(harmonizedHeadcount);
    const sizeLabel = headcountBucket
      ? `${headcountBucket} employees${headcountSuffix}`
      : "Employees not reported";
    const locationLabel = labelForLocation(primaryLocation);
    return (
      <>
        <CompanyModal
          open={open}
          setOpen={setOpen}
          companyDetail={company}
          imageData={imageData}
        />
        <Box
          data-testid="mobile-company-result-card"
          style={cardStyles}
          onClick={() => handlePopUp(company?.stagedId)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handlePopUp(company?.stagedId);
            }
          }}
        >
          <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <img
              style={{
                height: `${logoSize}px`,
                width: `${logoSize}px`,
                borderRadius: "10px",
                flexShrink: 0,
              }}
              src={imageData}
              alt={`${primaryName || "Company"} logo`}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  ...MOBILE_TYPE.cardTitle,
                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#111827",
                  mb: 0.35,
                }}
                noWrap
              >
                {primaryName}
              </Typography>
              {industryLabel ? (
                <Typography sx={{ ...MOBILE_TYPE.secondary, color: "text.secondary", mb: 0.5 }} noWrap>
                  {industryLabel}
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center", mb: 0.75 }}>
                {primaryWebsite ? (
                  <Typography
                    component="span"
                    sx={{
                      ...MOBILE_TYPE.helper,
                      color: theme.palette.mode === "dark" ? theme.palette.primary.main : "#4764F6",
                      fontWeight: 600,
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Link
                      href={`http://${primaryWebsite}`}
                      target="_blank"
                      rel="noreferrer"
                      underline="hover"
                      sx={{ color: "inherit", fontSize: "inherit", fontWeight: "inherit" }}
                    >
                      {primaryWebsite}
                    </Link>
                  </Typography>
                ) : null}
                <Tooltip title={headcountEstimated ? ESTIMATED_TOOLTIP : ""}>
                  <Typography sx={{ ...MOBILE_TYPE.helper, color: "text.secondary" }}>
                    {sizeLabel}
                  </Typography>
                </Tooltip>
                {locationLabel ? (
                  <Typography sx={{ ...MOBILE_TYPE.helper, color: "text.secondary" }} noWrap>
                    {locationLabel}
                  </Typography>
                ) : null}
              </Box>
              {descriptive?.[0] ? (
                <Typography
                  sx={{
                    ...MOBILE_TYPE.secondary,
                    color: theme.palette.mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(88,89,91,0.9)",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {descriptive[0].length > descriptionMaxLength
                    ? `${descriptive[0].slice(0, descriptionMaxLength)}...`
                    : descriptive[0]}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <CompanyModal
        open={open}
        setOpen={setOpen}
        companyDetail={company}
        imageData={imageData}
      />
      <ComingSoonDialog
        open={isUnimplemented}
        onClose={() => setIsUnimplemented(false)}
      />

      <Grid
        style={cardStyles}
        onClick={() => handlePopUp(company?.stagedId)}
      >
        <Grid container gap={containerGap}>
          <Grid item>
            <Grid container gap={itemGap} alignItems={"center"}>
              <Grid item>
                <img 
                  style={{
                    height: `${logoSize}px`,
                    width: `${logoSize}px`,
                    borderRadius: "12px" 
                  }}
                  src={imageData}
                  alt={`${primaryName || "Company"} logo`}
                />
              </Grid>
              <Grid item>
                <Grid container flexDirection={"column"} gap={compact ? "11px" : "15px"}>
                  <Grid item>
                    {/* EDIT BY ALI FOR DARK MODE - Updated Typography color to use theme */}
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
                        fontWeight: 700,
                        fontSize: titleFontSize,
                      }}
                    >
                      {primaryName}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Grid container gap={compact ? "17px" : "22px"} alignItems={"center"}>
                      <Grid item>
                        <Grid container gap={"4px"} alignItems={"center"}>
                          <Grid
                            item
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img 
                              width={compact ? "15px" : "20px"} 
                              height={compact ? "15px" : "20px"} 
                              src={Global} 
                              alt=""
                            />
                          </Grid>
                          {/* EDIT BY ALI FOR DARK MODE - Updated website link color for better clarity */}
                          <Grid item>
                            <Link
                              href={`http://${primaryWebsite}`}
                              target="_blank"
                              rel="noreferrer"
                              underline="none"
                              sx={{
                                fontWeight: 500,
                                fontSize: `${websiteFontSize}px`,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                cursor: "pointer",
                                textDecoration: "underline",
                                color: theme.palette.mode === "dark" ? theme.palette.primary.main : "#4764F6",
                              }}
                            >
                              {primaryWebsite}
                            </Link>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid container alignItems={"center"} gap={compact ? "10px" : "14px"}>
                          <BlackDotGrid item />
                          <Grid item>
                            <Tooltip title={headcountEstimated ? ESTIMATED_TOOLTIP : ""}>
                            <ChipCard
                              avatar={
                                <Avatar
                                  style={{ 
                                    height: compact ? "12px" : "16px", 
                                    width: compact ? "12px" : "16px" 
                                  }}
                                  alt="Natacha"
                                  src={people}
                                />
                              }
                              label={formatHeadcountBucket(harmonizedHeadcount)
                                ? `${formatHeadcountBucket(harmonizedHeadcount)} Employees${headcountSuffix}`
                                : "Employees not reported"}
                            />
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid container alignItems={"center"} gap={compact ? "10px" : "14px"}>
                          <BlackDotGrid item />
                          <Grid item>
                            {/* EDIT BY ALI FOR DARK MODE - Updated PlaceIcon color for better clarity */}
                            <ChipCardSecond
                              icon={<PlaceIcon sx={{ 
                                "&&": {
                                  color: theme.palette.mode === "dark" ? "#FFFFFF" : "#9900CE", 
                                  height: compact ? "14px" : "18px", 
                                  width: compact ? "14px" : "18px"
                                }
                              }}/>}
                              label={labelForLocation(primaryLocation)}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* EDIT BY ALI FOR DARK MODE - Updated description Typography color to use theme with better contrast */}
          <Grid item>
            <Typography
              style={{
                fontSize: `${descriptionFontSize}px`,
                lineHeight: descriptionLineHeight,
                fontWeight: "400",
                fontStyle: "normal",
                color: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.95)" : "rgba(88, 89, 91, 0.9)",
              }}
            >
              {(company && Array.isArray(descriptive) && descriptive.length > 0 && descriptive[0].length > descriptionMaxLength)
                ? descriptive[0]?.slice(0, descriptionMaxLength) + "..."
                : descriptive?.[0]
              }
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ListCard;
